// From constants/integrations, not the constants barrel: this module sits behind
// ProjectProvider, and the barrel would pull the workflow-template model and the
// entity-app constants in with it.
import { SCHEDULE_ONLY_INTEGRATION_SYSTEMS } from "constants/integrations";

export enum IntegrationType {
  AUTODESK = 1,
  PROCORE = 2,
  CMIC = 3,
  EXTERNAL = 4,
  BIM360 = 5,
  OPC = 6
}

// Gates the Add Integration dropdown only — NOT the grid, which renders whatever
// rows the server returns. A system listed here must also have an arm in
// project-integrations-list's onFormSubmit, or picking it leaves the panel
// spinning with no error.
export const activeSourceTypes = ["AUTODESK", "PROCORE", "BIM360", "OPC"];

// Systems with a *-integration-details view mounted in the integrations tabs.
// Anything else renders its name as plain text: the manage link unmounts the
// list into whichever view matches sharedState.system, so a system without one
// would navigate to a blank page.
export const systemsWithDetailsView = ["AUTODESK", "PROCORE", "OPC"];

/** Whether an integration row can open a details view (clickable name / row). */
export function canOpenIntegrationDetails(data: any): boolean {
  if (!data?.integration?.system) return false;
  const isFileBasedIntegration =
    data?.systemIntgConfigProperties?.INTEGRATION_METHOD === "file";
  const isOpcIntegration = data.integration.system === "OPC";
  // OPC is provisioned via method_type/file but has a details view.
  return (
    (!isFileBasedIntegration || isOpcIntegration) &&
    systemsWithDetailsView.includes(data.integration.system)
  );
}

/** Column-header tooltip for Account Name on subscription and project integration grids. */
export const ACCOUNT_NAME_HEADER_TOOLTIP =
  "Represents: Account in ACC, Company in Procore and Workspace in OPC.";

export const SUBMITTAL_SOURCE_SYSTEMS = ["AUTODESK", "PROCORE", "BIM360"];
export const SCHEDULE_SOURCE_SYSTEMS = ["OPC"];

export function simplifySubscriptionIntegrationConfigs(configs: any) {
  if (!configs) return null;
  return configs.map((config: any) => {
    const properties: any = {};
    config.integration_config.integration_config_properties.forEach(
      (prop: any) => {
        properties[prop.property_name] = prop.property_value;
      }
    );

    const systemIntgConfigProperties: any = {};
    config?.system_integration_config?.integration_config?.integration_config_properties?.forEach(
      (prop: any) => {
        systemIntgConfigProperties[prop.property_name] = prop.property_value;
      }
    );

    return {
      id: config.id,
      config_id: config.config_id,
      active: config.active,
      subscription_id: config.subscription_id,
      properties,
      integration_config_id: config.integration_config.id,
      integration: {
        system: config?.system_integration_config.integration?.system,
        availability_status:
          config?.system_integration_config?.integration?.availability_status,
        id: config?.system_integration_config?.integration?.id
      },
      systemIntgConfigProperties
    };
  });
}

export function simplifyProjectIntegrationConfigs(configs: any) {
  if (!configs) return null;
  return configs.map((config: any) => {
    const properties: any = {};
    config?.integration_config?.integration_config_properties?.forEach(
      (prop: any) => {
        properties[prop.property_name] = prop.property_value;
      }
    );
    // Optional like its siblings below: a config provisioned straight against a
    // project has no parent subscription config, and throwing here would take
    // down the whole tab through the useMemo rather than blank one cell.
    const subscriptionProperties: any = {};
    config?.subscription_integration_config?.integration_config?.integration_config_properties?.forEach(
      (prop: any) => {
        subscriptionProperties[prop.property_name] = prop.property_value;
      }
    );

    const systemIntgConfigProperties: any = {};
    config?.system_integration_config?.integration_config?.integration_config_properties?.forEach(
      (prop: any) => {
        systemIntgConfigProperties[prop.property_name] = prop.property_value;
      }
    );

    return {
      id: config.id,
      config_id: config.config_id,
      active: config.active,
      subscription_id: config.subscription_integration_config_id,
      properties,
      subscriptionProperties,
      integration_config_id: config?.integration_config?.id,
      integration: {
        system: config?.system_integration_config?.integration?.system,
        availability_status:
          config?.system_integration_config?.integration?.availability_status,
        id: config?.system_integration_config?.integration?.id
      },
      systemIntgConfigProperties
    };
  });
}

// Both shapes of an integration config are in circulation: the simplified copy
// this module produces, and a raw project_integration_configs row straight off
// the query. Undefined for a row that names no system at all — treated below as
// "cannot rule it out", which keeps single-integration projects and the fixtures
// that omit the field behaving exactly as before.
const getIntegrationSystem = (config: any) =>
  config?.integration?.system ??
  config?.system_integration_config?.integration?.system;

/**
 * The integration config the submittal features are about.
 *
 * NOT `configs[0]`. project_integration_configs holds one row per system and a
 * project can now hold more than one — a submittal integration (ACC / Procore /
 * BIM360 / file-based) alongside the schedule-only OPC config. That array comes
 * back unordered, so `[0]` is a coin toss, and losing it points every submittal
 * question at OPC's properties: no SYNC_INTERVAL, so the import cooldown adds
 * `undefined` minutes to a valid date and renders "You can only import again
 * after Invalid Date", with `isSameOrAfter` false forever behind it.
 *
 * Returns null when the project has no submittal integration at all (OPC only),
 * which callers already handle as "not in integration mode".
 *
 * Accepts either shape — see getIntegrationSystem.
 */
export function getSubmittalIntegrationConfig(configs: any) {
  if (!configs?.length) return null;
  return (
    configs.find(
      (config: any) =>
        !SCHEDULE_ONLY_INTEGRATION_SYSTEMS.includes(
          getIntegrationSystem(config)
        )
    ) ?? null
  );
}

export function getAccSubmittalIntegrationSystem(
  projectIntegrationConfig: any
) {
  const submittalConfig = getSubmittalIntegrationConfig(
    projectIntegrationConfig
  );
  return (
    submittalConfig?.integration?.system ??
    submittalConfig?.system_integration_config?.integration?.system
  );
}

export function isAccSubmittalProject(projectIntegrationConfig: any): boolean {
  const system = getAccSubmittalIntegrationSystem(projectIntegrationConfig);
  return system === "AUTODESK" || system === "BIM360";
}

/**
 * Whether the submittal features should behave as integrated.
 *
 * The project's own mode is no longer the whole answer. OPC feeds the schedule
 * and nothing else, so a project can sit in integration mode with no integration
 * behind its submittals — and those submittals still have to be creatable and
 * importable by hand, which integration mode otherwise hides. Answering off the
 * project mode alone leaves such a project with no way to add a submittal at all:
 * the file-based Import Log needs a config with INTEGRATION_METHOD "file", and
 * OPC is "api".
 *
 * A missing config list means the configs have not landed yet, NOT that there is
 * no submittal integration — the caller gets "integrated" so a real ACC or
 * Procore project never flashes its manual create controls while loading.
 */
export function isSubmittalIntegrationMode(
  isProjectInIntegrationMode: boolean,
  projectIntegrationConfig: any
) {
  if (!isProjectInIntegrationMode) return false;
  if (!projectIntegrationConfig) return true;
  return !!getSubmittalIntegrationConfig(projectIntegrationConfig);
}

export function simplifySystemIntegrationConfigs(originalArray: any) {
  if (!originalArray) return null;
  return originalArray.map((config: any) => {
    const optimizedConfig: any = {
      system: config?.integration?.system,
      integration_config_properties: {}
    };

    config.integration_config.integration_config_properties.forEach(
      (property: any) => {
        optimizedConfig.integration_config_properties[property.property_name] =
          property.property_value;
      }
    );

    return optimizedConfig;
  });
}

export const getIntegrationSystemAUthorizerDetails = (
  propertiesObject: any
) => {
  const name =
    propertiesObject.AUTHORIZER_PROCORE_NAME ||
    propertiesObject.SUBSCRIPTION_AUTHORIZER_AUTODESK_NAME;
  const email =
    propertiesObject.AUTHORIZER_PROCORE_EMAIL ||
    propertiesObject.SUBSCRIPTION_AUTHORIZER_AUTODESK_EMAIL;
  return { name, email };
};

export const getCIQAUthorizerDetails = (propertiesObject: any) => {
  const name =
    propertiesObject.AUTHORIZER_CIQ_NAME ||
    propertiesObject.SUBSCRIPTION_AUTHORIZER_CIQ_NAME ||
    "";
  const email =
    propertiesObject.AUTHORIZER_CIQ_EMAIL ||
    propertiesObject.SUBSCRIPTION_AUTHORIZER_CIQ_EMAIL ||
    "";
  return { name, email };
};

export const getAddedByLabel = (propertiesObject: any) => {
  const { name, email } = getCIQAUthorizerDetails(propertiesObject || {});
  return email ? `${name} (${email})` : name;
};

export const getAddedByAuthorizedAt = (propertiesObject: any) => {
  return propertiesObject?.LAST_AUTHORIZED || "";
};

// Literal rather than IntegrationAuthStates: this module must stay off the
// constants barrel (see the import note at the top).
const REAUTHORIZATION_REQUIRED = "REAUTHORIZATION_REQUIRED";

// Multi-workspace OPC setup names each config "<Name> (<Workspace>)". Only a
// suffix matching the config's own workspace is dropped, so a user-typed
// parenthetical survives.
export const getOpcBaseIntegrationName = (config: any): string => {
  const name: string = config?.properties?.INTEGRATION_NAME ?? "";
  const workspace: string = config?.properties?.OPC_WORKSPACE_NAME ?? "";
  const suffix = ` (${workspace})`;
  return workspace && name.endsWith(suffix)
    ? name.slice(0, -suffix.length)
    : name;
};

/**
 * Collapses OPC configs added by the same user into one row whose Account Name
 * lists every workspace, comma separated. Non-OPC configs, and OPC configs with
 * no resolvable "Added By", pass through untouched.
 *
 * The merged row keeps the oldest config's identity (id, auth, endpoint), shows
 * its base name, carries a re-auth flag if any member needs one, and exposes
 * all members on `opcMergedConfigs`. Rows keep the position of their first
 * member, so the grid order is otherwise unchanged.
 */
export function mergeOpcIntegrationsByAddedBy(configs?: Array<any> | null) {
  if (!configs) return configs;

  const groups = new Map<string, Array<any>>();
  configs.forEach((config) => {
    if (config?.integration?.system !== "OPC") return;
    const addedBy = getAddedByLabel(config?.properties);
    if (!addedBy) return;
    const group = groups.get(addedBy);
    if (group) group.push(config);
    else groups.set(addedBy, [config]);
  });

  const emitted = new Set<string>();
  return configs.flatMap((config) => {
    const addedBy =
      config?.integration?.system === "OPC"
        ? getAddedByLabel(config?.properties)
        : "";
    const group = addedBy ? groups.get(addedBy) : undefined;
    if (!group || group.length < 2) return [config];
    if (emitted.has(addedBy)) return [];
    emitted.add(addedBy);

    const members = [...group].sort((a, b) =>
      String(getAddedByAuthorizedAt(a?.properties)).localeCompare(
        String(getAddedByAuthorizedAt(b?.properties))
      )
    );
    const primary = members[0];
    const workspaceNames = Array.from(
      new Set(
        members
          .map((member) => member?.properties?.OPC_WORKSPACE_NAME)
          .filter(Boolean)
      )
    );
    const needsReauth = members.some(
      (member) =>
        member?.properties?.SUBSCRIPTION_CONFIG_AUTH_STATE ===
          REAUTHORIZATION_REQUIRED ||
        member?.properties?.PROJECT_CONFIG_AUTH_STATE ===
          REAUTHORIZATION_REQUIRED
    );

    return [
      {
        ...primary,
        properties: {
          ...primary.properties,
          INTEGRATION_NAME: getOpcBaseIntegrationName(primary),
          OPC_WORKSPACE_NAME: workspaceNames.join(", "),
          ...(needsReauth && {
            SUBSCRIPTION_CONFIG_AUTH_STATE: REAUTHORIZATION_REQUIRED
          })
        },
        opcMergedConfigs: members
      }
    ];
  });
}

export type TOpcAccount = {
  id: string;
  name: string;
  configs: Array<any>;
};

/**
 * One entry per OPC row of the subscription Integrations grid, so project
 * setup offers the same "accounts" the subscription admin sees there.
 */
export function getOpcAccounts(configs?: Array<any> | null): TOpcAccount[] {
  const opcConfigs = (configs || []).filter(
    (config: any) => config?.integration?.system === "OPC"
  );
  return (mergeOpcIntegrationsByAddedBy(opcConfigs) || []).map((row: any) => ({
    id: String(row.id),
    name: row.properties?.INTEGRATION_NAME ?? "",
    configs: row.opcMergedConfigs ?? [row]
  }));
}

/**
 * Build configure-screen authData in the same shape ACC gets from
 * oauth_operations (user_info.given_name/family_name/email + created_by_user).
 * OPC basic-auth often omits user_info — fill from the login username.
 */
export function buildOpcConfigureAuthData(
  authResponseData: any,
  opcUsername: string,
  currentUser?: Record<string, any> | null
) {
  const data =
    authResponseData?.opc_auth_operation ||
    authResponseData?.auth_operation ||
    authResponseData?.data ||
    authResponseData ||
    {};

  const parsedOutput =
    typeof data.output === "string"
      ? (() => {
          try {
            return JSON.parse(data.output) || {};
          } catch {
            return {};
          }
        })()
      : data.output || {};

  const existingUserInfo = parsedOutput.user_info || {};
  const emailFromUsername = opcUsername?.includes("@") ? opcUsername : "";
  const nameFromUsername = opcUsername?.includes("@")
    ? opcUsername.split("@")[0]
    : opcUsername || "";

  const email =
    existingUserInfo.email ||
    existingUserInfo.login ||
    emailFromUsername ||
    opcUsername ||
    "";
  const givenName =
    existingUserInfo.given_name ||
    existingUserInfo.name ||
    nameFromUsername ||
    "";
  const familyName = existingUserInfo.family_name || "";

  const firstName =
    data.created_by_user?.first_name ||
    currentUser?.["first-name"] ||
    currentUser?.first_name ||
    currentUser?.given_name ||
    "";
  const lastName =
    data.created_by_user?.last_name ||
    currentUser?.["last-name"] ||
    currentUser?.last_name ||
    currentUser?.family_name ||
    "";
  const addedByEmail = data.created_by_user?.email || currentUser?.email || "";

  return {
    ...data,
    id: data.id || authResponseData?.id,
    updated_at: data.updated_at || data.created_at || new Date().toISOString(),
    output: {
      ...parsedOutput,
      user_info: {
        ...existingUserInfo,
        given_name: givenName,
        family_name: familyName,
        email
      }
    },
    created_by_user: {
      first_name: firstName,
      last_name: lastName,
      email: addedByEmail
    }
  };
}
